import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../api/client'
import { ROUTES } from '../routes'
import Button from './Button'
import IntegrationEventList from './IntegrationEventList'
import { Accordion, EmptyState, InlineAlert, PageHeader, Skeleton, Status } from './ui'
import copyIcon from '../assets/figma/copy.svg'
import editIcon from '../assets/figma/edit.svg'
import plusIcon from '../assets/figma/plus.svg'
import { FINTABLO_FIELD_LABELS } from './ScenarioEditorParts'
import { exportResult, exportStatus, periodLabel } from './historyExportFormat'
import '../styles/connector-hub.css'
import '../styles/history-export.css'

const SECTION_STORAGE_PREFIX = 'connector-hub:open-section:'
const SECTION_NAMES = ['references', 'scenarios', 'exports', 'events']
// Выгрузка прошлых периодов есть у этих источников (history-export).
const EXPORT_CONNECTORS = new Set(['bitrix24', 'amocrm'])

const SCENARIO_STATUSES = {
  draft: { label: 'Не активно', tone: 'error' },
  active: { label: 'Активно', tone: 'success' },
  paused: { label: 'Не активно', tone: 'error' }
}

const TRIGGER_LABELS = {
  ONCRMDEALADD: 'Сделка создана',
  ONCRMDEALUPDATE: 'Сделка изменена',
  add_lead: 'Сделка создана',
  update_lead: 'Сделка изменена'
}

function readOpenSections (slug) {
  try {
    const stored = window.localStorage.getItem(`${SECTION_STORAGE_PREFIX}${slug}`)
    if (!stored) return {}
    try {
      const sections = JSON.parse(stored)
      if (Array.isArray(sections)) {
        return Object.fromEntries(
          sections.filter(section => SECTION_NAMES.includes(section)).map(section => [section, true])
        )
      }
    } catch (_) {}
    return SECTION_NAMES.includes(stored) ? { [stored]: true } : {}
  } catch (_) {
    return {}
  }
}

function storeOpenSections (slug, sections) {
  try {
    const key = `${SECTION_STORAGE_PREFIX}${slug}`
    const openSections = SECTION_NAMES.filter(section => sections[section])
    if (openSections.length > 0) window.localStorage.setItem(key, JSON.stringify(openSections))
    else window.localStorage.removeItem(key)
  } catch (_) {}
}

function statusFor (status, statuses) {
  return statuses[status] || { label: 'Неизвестный статус', tone: 'neutral' }
}

function ReferencesSection ({ references, slug }) {
  if (references.length === 0) {
    return <EmptyState title='Справочники пока недоступны' description='Для этого источника нет доступных справочников.' />
  }

  return (
    <div className='connector-hub__section-content'>
      <p className='connector-hub__section-summary'>Сотрудники и контрагенты синхронизируются отдельно — только выбранный справочник.</p>
      <ul className='connector-hub__list connector-hub__reference-list'>
        {references.map(reference => (
          <li className='connector-hub__list-row' key={reference.type}>
            <div className='connector-hub__reference-title'>
              <span className='connector-hub__list-title'>{reference.label}</span>
              {reference.counts && (
                <span className='connector-hub__reference-counts'>
                  Всего: {reference.counts.total} · Сопоставлено: {reference.counts.mapped} · Не сопоставлено: {reference.counts.unmapped}
                </span>
              )}
            </div>
            <Link
              aria-label={`Открыть справочник ${reference.label}`}
              className='connector-hub__reference-link'
              to={ROUTES.REFERENCE(slug, reference.type)}
            >
              Открыть редактор <span aria-hidden='true'>→</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

function ScenariosSection ({
  actionStates,
  bootstrapState,
  connection,
  onBootstrapRetry,
  onCreate,
  onDelete,
  onDuplicate,
  onStatusChange,
  scenarios,
  slug
}) {
  if (!connection) {
    return <EmptyState title='Сначала подключите источник' description='После подключения здесь появятся доступные сценарии.' />
  }

  return (
    <div className='connector-hub__section-content'>
      <p className='connector-hub__prerequisites' role='note'>Перед запуском проверьте справочники сотрудников и контрагентов.</p>
      {scenarios.length === 0 && bootstrapState.status === 'loading' && (
        <p className='connector-hub__bootstrap-status' role='status'>Создаём первый сценарий…</p>
      )}
      {scenarios.length === 0 && bootstrapState.status === 'error' && (
        <InlineAlert title='Не удалось создать первый сценарий' tone='error'>
          <span>{bootstrapState.error}</span>
          <button
            className='ui-button ui-button--ghost connector-hub__retry'
            type='button'
            onClick={onBootstrapRetry}
          >
            Повторить
          </button>
        </InlineAlert>
      )}
      {scenarios.filter(scenario => actionStates[scenario.id]?.error).map(scenario => (
        <InlineAlert key={scenario.id} title='Не удалось изменить сценарий' tone='error'>
          {scenario.name}: {actionStates[scenario.id].error}
        </InlineAlert>
      ))}
      {actionStates.create?.error && (
        <InlineAlert title='Не удалось создать сценарий' tone='error'>
          <span>{actionStates.create.error}</span>
          <button
            className='ui-button ui-button--ghost connector-hub__retry'
            type='button'
            onClick={onCreate}
          >
            Повторить
          </button>
        </InlineAlert>
      )}
      {scenarios.length === 0
        ? bootstrapState.status === 'idle'
          ? <EmptyState title='Сценариев пока нет' description='Первый сценарий будет создан автоматически.' />
          : null
        : (
          <ul className='connector-hub__list'>
            {scenarios.map(scenario => {
              const status = statusFor(scenario.status, SCENARIO_STATUSES)
              const triggerLabel = TRIGGER_LABELS[scenario.trigger_event] ||
                (scenario.status === 'draft' ? 'не настроен' : 'настроен')
              const toggleLabel = scenario.status === 'active' ? 'Приостановить' : 'Запустить'
              const canToggle = scenario.status === 'active' ||
                (['draft', 'paused'].includes(scenario.status) && connection.status === 'active')
              const loading = Boolean(actionStates[scenario.id]?.loading)
              const action = actionStates[scenario.id]?.action
              return (
                <li className='connector-hub__list-row connector-hub__scenario-row connector-hub__scenario-card' key={scenario.id}>
                  <div className='connector-hub__scenario-main'>
                    <span className='connector-hub__scenario-trigger'>триггер: «{triggerLabel}»</span>
                    <span className='connector-hub__list-title'>{scenario.name}</span>
                  </div>
                  <div className='connector-hub__scenario-actions'>
                    <Status className='connector-hub__scenario-status' tone={status.tone}>{status.label}</Status>
                    <div className='connector-hub__scenario-controls'>
                      <Link className='connector-hub__scenario-link' to={ROUTES.SCENARIO(slug, scenario.id)}>
                        <img alt='' src={editIcon} /> Редактировать
                      </Link>
                      <Button
                        aria-label='Дублировать'
                        className='connector-hub__scenario-link'
                        disabled={loading}
                        loading={loading && action === 'duplicate'}
                        onClick={() => onDuplicate(scenario)}
                        variant='ghost'
                      >
                        <img alt='' src={copyIcon} /> Дублировать
                      </Button>
                      <Button
                        aria-label={`Удалить сценарий ${scenario.name}`}
                        className='connector-hub__scenario-link connector-hub__scenario-delete'
                        disabled={loading || scenario.status === 'active'}
                        onClick={() => onDelete(scenario)}
                        title={scenario.status === 'active' ? 'Сначала остановите активный сценарий.' : undefined}
                        variant='ghost'
                      >
                        Удалить
                      </Button>
                    </div>
                    <Button
                      aria-pressed={scenario.status === 'active'}
                      className='connector-hub__scenario-toggle'
                      disabled={!canToggle}
                      loading={loading && action === 'status'}
                      onClick={() => onStatusChange(scenario)}
                      title={canToggle ? undefined : 'Для запуска требуется активное подключение.'}
                      variant='ghost'
                    >
                      <span className='connector-hub__toggle-knob' aria-hidden='true' />
                      <span className='ui-sr-only'>{toggleLabel}</span>
                    </Button>
                  </div>
                </li>
              )
            })}
          </ul>
          )}
      <div className='connector-hub__add-scenario'>
        <button
          aria-label='Создать сценарий'
          className='ui-button ui-button--solid'
          disabled={!['bitrix24', 'amocrm'].includes(slug) || actionStates.create?.loading || connection.status !== 'active'}
          onClick={onCreate}
          title={['bitrix24', 'amocrm'].includes(slug) ? undefined : 'Создание сценария для этого источника пока недоступно.'}
          type='button'
        >
          <img alt='' src={plusIcon} /> Добавить сценарий
        </button>
      </div>
    </div>
  )
}

// «Прошлые периоды» (ADR R20): выгрузки сделок, созданных до подключения.
function ExportsSection ({ connection, creating, createError, onCreate, onRetry, slug, state }) {
  if (!connection) {
    return <EmptyState title='Сначала подключите источник' description='После подключения здесь можно выгрузить сделки за прошлые периоды.' />
  }
  return (
    <div className='connector-hub__section-content'>
      <div className='history-export-list__head'>
        <p className='connector-hub__section-summary'>Переносят в Финтабло сделки, созданные в {slug === 'amocrm' ? 'amoCRM' : 'Bitrix24'} до подключения. Сделки, которые уже есть в Финтабло, пропускаются.</p>
        <button
          className='ui-button ui-button--solid'
          disabled={creating || connection.status !== 'active'}
          onClick={onCreate}
          type='button'
        >
          Новая выгрузка
        </button>
      </div>
      {createError && <InlineAlert title='Не удалось создать выгрузку' tone='error'>{createError}</InlineAlert>}
      {state.status === 'loading' && <Skeleton aria-label='Загрузка выгрузок' />}
      {state.status === 'error' && (
        <InlineAlert title='Не удалось загрузить выгрузки' tone='error'>
          <span>{state.error}</span>
          <button aria-label='Повторить загрузку выгрузок' className='ui-button ui-button--ghost connector-hub__retry' type='button' onClick={onRetry}>Повторить</button>
        </InlineAlert>
      )}
      {state.status === 'success' && state.items.length === 0 && (
        <EmptyState title='Выгрузок пока нет' description='Выберите период и проверьте настройки на трёх пробных сделках — потом выгрузите остальные.' />
      )}
      {state.status === 'success' && state.items.length > 0 && (
        <div className='history-export-list__table-wrap'>
          <table className='history-export-list__table'>
            <thead>
              <tr><th>Период создания сделок</th><th>Статус</th><th>Итог</th><th>Отслеживается</th></tr>
            </thead>
            <tbody>
              {state.items.map(item => {
                const status = exportStatus(item.status)
                const result = exportResult(item)
                return (
                  <tr key={item.id}>
                    <td><Link to={ROUTES.EXPORT(slug, item.id)}>{periodLabel(item.period_from, item.period_to)}</Link></td>
                    <td><Status tone={status.tone}>{status.label}</Status></td>
                    <td>{result.main}{result.extra && <small>{result.extra}</small>}</td>
                    <td>{(item.tracked_fields || []).map(field => FINTABLO_FIELD_LABELS[field] || field).join(', ') || '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function EventsSection ({ slug, state, onRetry }) {
  return (
    <div className='connector-hub__events'>
      <IntegrationEventList error={state.error} items={state.items} loading={state.status === 'idle' || state.status === 'loading'} onRetry={onRetry} />
      <Link className='connector-hub__events-link' to={ROUTES.LOGS(slug)}>Открыть полный журнал</Link>
    </div>
  )
}

export default function ConnectorHub () {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [hub, setHub] = useState(null)
  const [error, setError] = useState(null)
  const [openSections, setOpenSections] = useState(() => readOpenSections(slug))
  const [events, setEvents] = useState({ status: 'idle', items: [], error: null })
  const [exportsState, setExportsState] = useState({ status: 'idle', items: [], error: null })
  const [exportCreate, setExportCreate] = useState({ loading: false, error: null })
  const [scenarioActions, setScenarioActions] = useState({})
  const [bootstrapState, setBootstrapState] = useState({ status: 'idle', error: null })
  const [retryingRegistration, setRetryingRegistration] = useState(false)
  const [retryError, setRetryError] = useState(null)
  const hubRequestId = useRef(0)
  const scenarioActionRequestIds = useRef({})
  const scenarioActionGeneration = useRef(0)
  const bootstrapRequestId = useRef(0)
  const bootstrapInFlight = useRef(false)
  const createInFlight = useRef(false)

  const loadHub = useCallback(() => {
    const requestId = ++hubRequestId.current
    setHub(null)
    setError(null)
    api.getConnectorHub(slug)
      .then(result => { if (requestId === hubRequestId.current) setHub(result) })
      .catch(err => { if (requestId === hubRequestId.current) setError(err.message) })
  }, [slug])

  // Повтор регистрации вебхука: токены сохранены, поэтому переподключать
  // источник не нужно. Успех возвращает подключение в рабочее состояние —
  // перечитываем хаб, чтобы предупреждение исчезло само.
  const retryWebhookRegistration = useCallback(async () => {
    const connectionId = hub?.connection?.id
    if (!connectionId) return
    setRetryingRegistration(true)
    setRetryError(null)
    try {
      await api.retryWebhookRegistration(connectionId)
      loadHub()
    } catch (err) {
      setRetryError(err.message)
    } finally {
      setRetryingRegistration(false)
    }
  }, [hub?.connection?.id, loadHub])

  const loadEvents = useCallback(() => {
    setEvents({ status: 'loading', items: [], error: null })
    api.getIntegrationEvents({ connector: slug, limit: 5 })
      .then(result => setEvents({ status: 'success', items: result.items || [], error: null }))
      .catch(err => setEvents({ status: 'error', items: [], error: err.message }))
  }, [slug])

  const loadExports = useCallback(() => {
    setExportsState({ status: 'loading', items: [], error: null })
    api.listExports(slug)
      .then(result => setExportsState({ status: 'success', items: result.exports || [], error: null }))
      .catch(err => setExportsState({ status: 'error', items: [], error: err.message }))
  }, [slug])

  async function createExport () {
    if (exportCreate.loading) return
    setExportCreate({ loading: true, error: null })
    try {
      const result = await api.createExport(slug)
      navigate(ROUTES.EXPORT(slug, result.export.id))
    } catch (err) {
      setExportCreate({ loading: false, error: err.message })
    }
  }

  const bootstrapFirstScenario = useCallback(async () => {
    if (bootstrapInFlight.current) return
    bootstrapInFlight.current = true
    const requestId = ++bootstrapRequestId.current
    setBootstrapState({ status: 'loading', error: null })
    try {
      const result = await api.bootstrapScenario(slug)
      if (requestId !== bootstrapRequestId.current) return
      setHub(current => {
        if (!current || current.scenarios.some(item => item.id === result.scenario.id)) return current
        const scenarios = [...current.scenarios, result.scenario]
        const summary = current.summary
          ? {
              ...current.summary,
              scenarios: {
                ...current.summary.scenarios,
                total: scenarios.length,
                active: scenarios.filter(item => item.status === 'active').length
              }
            }
          : current.summary
        return { ...current, scenarios, summary }
      })
      setBootstrapState({ status: 'success', error: null })
    } catch (err) {
      if (requestId === bootstrapRequestId.current) {
        setBootstrapState({ status: 'error', error: err.message })
      }
    } finally {
      if (requestId === bootstrapRequestId.current) bootstrapInFlight.current = false
    }
  }, [slug])

  useEffect(() => {
    scenarioActionGeneration.current += 1
    bootstrapRequestId.current += 1
    bootstrapInFlight.current = false
    createInFlight.current = false
    setOpenSections(readOpenSections(slug))
    setEvents({ status: 'idle', items: [], error: null })
    setExportsState({ status: 'idle', items: [], error: null })
    setExportCreate({ loading: false, error: null })
    setScenarioActions({})
    setBootstrapState({ status: 'idle', error: null })
    scenarioActionRequestIds.current = {}
    loadHub()
    return () => {
      hubRequestId.current += 1
      scenarioActionGeneration.current += 1
      bootstrapRequestId.current += 1
      bootstrapInFlight.current = false
      createInFlight.current = false
    }
  }, [loadHub, slug])

  useEffect(() => {
    if (
      hub?.connector.slug === slug &&
      hub.connection?.status === 'active' &&
      hub.scenarios.length === 0 &&
      bootstrapState.status === 'idle'
    ) {
      bootstrapFirstScenario()
    }
  }, [bootstrapFirstScenario, bootstrapState.status, hub, slug])

  useEffect(() => {
    if (hub?.connector.slug === slug && openSections.events && events.status === 'idle') loadEvents()
  }, [events.status, hub, loadEvents, openSections.events, slug])

  useEffect(() => {
    if (
      hub?.connector.slug === slug && hub.connection && EXPORT_CONNECTORS.has(slug) &&
      openSections.exports && exportsState.status === 'idle'
    ) loadExports()
  }, [exportsState.status, hub, loadExports, openSections.exports, slug])

  function setSectionOpen (section, open) {
    setOpenSections(current => {
      const next = { ...current, [section]: open }
      storeOpenSections(slug, next)
      return next
    })
  }

  async function changeScenarioStatus (scenario) {
    const generation = scenarioActionGeneration.current
    const requestId = (scenarioActionRequestIds.current[scenario.id] || 0) + 1
    scenarioActionRequestIds.current[scenario.id] = requestId
    const isCurrent = () => generation === scenarioActionGeneration.current &&
      scenarioActionRequestIds.current[scenario.id] === requestId
    setScenarioActions(current => ({
      ...current,
      [scenario.id]: { loading: true, action: 'status', error: null }
    }))
    try {
      const result = scenario.status === 'active'
        ? await api.pauseScenario(scenario.id)
        : await api.startScenario(scenario.id)
      if (!isCurrent()) return
      setHub(current => {
        if (!current) return current
        const scenarios = current.scenarios.map(item =>
          item.id === result.scenario.id ? { ...item, ...result.scenario } : item
        )
        const summary = current.summary
          ? {
              ...current.summary,
              scenarios: {
                ...current.summary.scenarios,
                active: scenarios.filter(item => item.status === 'active').length
              }
            }
          : current.summary
        return { ...current, scenarios, summary }
      })
      setScenarioActions(current => ({
        ...current,
        [scenario.id]: { loading: false, action: null, error: null }
      }))
    } catch (err) {
      if (isCurrent()) {
        setScenarioActions(current => ({
          ...current,
          [scenario.id]: { loading: false, action: null, error: err.message }
        }))
      }
    }
  }

  async function duplicateScenario (scenario) {
    const generation = scenarioActionGeneration.current
    const requestId = (scenarioActionRequestIds.current[scenario.id] || 0) + 1
    scenarioActionRequestIds.current[scenario.id] = requestId
    const isCurrent = () => generation === scenarioActionGeneration.current &&
      scenarioActionRequestIds.current[scenario.id] === requestId
    setScenarioActions(current => ({
      ...current,
      [scenario.id]: { loading: true, action: 'duplicate', error: null }
    }))
    try {
      const result = await api.duplicateScenario(scenario.id)
      if (!isCurrent()) return
      setHub(current => current
        ? { ...current, scenarios: [...current.scenarios, result.scenario] }
        : current)
      setScenarioActions(current => ({
        ...current,
        [scenario.id]: { loading: false, action: null, error: null }
      }))
    } catch (err) {
      if (isCurrent()) {
        setScenarioActions(current => ({
          ...current,
          [scenario.id]: { loading: false, action: null, error: err.message }
        }))
      }
    }
  }

  async function createScenario () {
    if (createInFlight.current) return
    createInFlight.current = true
    setScenarioActions(current => ({
      ...current,
      create: { loading: true, action: 'create', error: null }
    }))
    try {
      const result = await api.createScenario(slug)
      setHub(current => current
        ? { ...current, scenarios: [...current.scenarios, result.scenario] }
        : current)
      navigate(ROUTES.SCENARIO(slug, result.scenario.id))
    } catch (err) {
      setScenarioActions(current => ({
        ...current,
        create: { loading: false, action: null, error: err.message }
      }))
    } finally {
      createInFlight.current = false
    }
  }

  async function deleteScenario (scenario) {
    if (!window.confirm(`Удалить сценарий «${scenario.name}»? Это действие нельзя отменить.`)) return
    setScenarioActions(current => ({
      ...current,
      [scenario.id]: { loading: true, action: 'delete', error: null }
    }))
    try {
      await api.deleteScenario(scenario.id)
      setHub(current => {
        if (!current) return current
        const scenarios = current.scenarios.filter(item => item.id !== scenario.id)
        const summary = current.summary
          ? {
              ...current.summary,
              scenarios: {
                ...current.summary.scenarios,
                total: scenarios.length,
                active: scenarios.filter(item => item.status === 'active').length
              }
            }
          : current.summary
        return { ...current, scenarios, summary }
      })
      setScenarioActions(current => {
        const next = { ...current }
        delete next[scenario.id]
        return next
      })
    } catch (err) {
      setScenarioActions(current => ({
        ...current,
        [scenario.id]: { loading: false, action: null, error: err.message }
      }))
    }
  }

  if (error) {
    return (
      <InlineAlert title='Не удалось загрузить коннектор' tone='error'>
        <span>{error}</span>
        <button className='ui-button ui-button--ghost connector-hub__retry' type='button' onClick={loadHub}>Повторить</button>
      </InlineAlert>
    )
  }

  if (!hub) {
    return <Skeleton className='connector-hub__header-skeleton' aria-label='Загрузка коннектора' />
  }

  return (
    <div className='connector-hub'>
      <Link className='connector-hub__back' to={ROUTES.INTEGRATIONS}>Источники</Link>
      <PageHeader
        title={hub.connector.name}
        description={hub.connection?.display_name || 'Источник не подключён'}
      />
      {hub.connection?.health === 'action_required' && (
        <InlineAlert title='Требуется действие' tone='warning'>
          {/* Причину знает сервер; молчать про неё — тот же дефект, что чиним:
              подключение выглядит исправным и не работает. */}
          <span>{hub.connection.error || 'Подключение требует внимания. Проверьте источник и восстановите подключение.'}</span>
          <button
            className='ui-button ui-button--ghost connector-hub__retry'
            type='button'
            disabled={retryingRegistration}
            onClick={retryWebhookRegistration}
          >
            {retryingRegistration ? 'Повторяем…' : 'Повторить регистрацию'}
          </button>
          {retryError && <span>{retryError}</span>}
        </InlineAlert>
      )}
      <section className='connector-hub__sections' aria-label='Разделы коннектора'>
        <Accordion
          ariaLabel='Справочники'
          className='connector-hub__accordion'
          title='Справочник'
          open={Boolean(openSections.references)}
          onOpenChange={open => setSectionOpen('references', open)}
        >
          <ReferencesSection references={hub.references} slug={slug} />
        </Accordion>
        <Accordion ariaLabel='Сценарии' className='connector-hub__accordion' title='Сценарии интеграции' open={Boolean(openSections.scenarios)} onOpenChange={open => setSectionOpen('scenarios', open)}>
          <ScenariosSection
            actionStates={scenarioActions}
            bootstrapState={bootstrapState}
            connection={hub.connection}
            onBootstrapRetry={bootstrapFirstScenario}
            onCreate={createScenario}
            onDelete={deleteScenario}
            onDuplicate={duplicateScenario}
            onStatusChange={changeScenarioStatus}
            scenarios={hub.scenarios}
            slug={slug}
          />
        </Accordion>
        {EXPORT_CONNECTORS.has(slug) && (
          <Accordion ariaLabel='Прошлые периоды' className='connector-hub__accordion' title='Прошлые периоды' open={Boolean(openSections.exports)} onOpenChange={open => setSectionOpen('exports', open)}>
            <ExportsSection
              connection={hub.connection}
              createError={exportCreate.error}
              creating={exportCreate.loading}
              onCreate={createExport}
              onRetry={loadExports}
              slug={slug}
              state={exportsState}
            />
          </Accordion>
        )}
        <Accordion ariaLabel='Лог событий' className='connector-hub__accordion' title='Лог событий' open={Boolean(openSections.events)} onOpenChange={open => setSectionOpen('events', open)}>
          <EventsSection slug={slug} state={events} onRetry={loadEvents} />
        </Accordion>
      </section>
    </div>
  )
}
